import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { cubicBezier, easeInOut, useMotionValueEvent, useTransform } from "motion/react";
import { use, useContext, useEffect, useState } from "react";
import { motion } from "motion/react";

export default function AboutTimeline({ className }: { className?: string }) {
  const [showDots, setShowDots] = useState(false);
  const [boxAspectRatio, setBoxAspectRatio] = useState(window.innerWidth / window.innerHeight);

  /**
   * Animate Timeline Bar
   */
  // Calculate the amount of y offset based on screen size
  const endYOffset = window.innerHeight * 0.04; // 4svh
  const scrollYProgress = useContext(ScrollAnimContext);
  const barStyles = {
    width: useTransform(scrollYProgress, [0, 0.09], ["0svw", "300svw"], { ease: cubicBezier(0.64, 0, 0.78, 0) }),
    left: useTransform(scrollYProgress, [0.18, 0.63], ["0svw", "-500svw"], { ease: easeInOut }),
    scale: useTransform(scrollYProgress, [0.216, 0.63], [1, 3], { ease: easeInOut }),
    y: useTransform(scrollYProgress, [0.234, 0.63], [0, -endYOffset], { ease: easeInOut }),
  };

  /**
   * Animate last timeline item
   */
  const lineHeight = useTransform(scrollYProgress, [0.63, 0.684], ["0svh", "2svh"], { ease: easeInOut });
  const lastItemStyles = {
    opacity: useTransform(scrollYProgress, [0.666, 0.792], [0, 1], { ease: easeInOut }),
    scale: useTransform(scrollYProgress, [0.81, 1], [1, 10], { ease: cubicBezier(0.32, 0, 0.66, -0.32) }),
    border: useTransform(scrollYProgress, [0.666, 0.99, 1], ["1px solid white", "1px solid white", "none"]), // We remove the border at the very end to avoid a weird border popping up as the user scrolls
    boxShadow: useTransform(scrollYProgress, [0.87, 1], ["0 0 0 100svw rgba(0, 0, 0, 0)", "0 0 0 100svw rgba(0, 0, 0, 1)"], {
      ease: easeInOut,
    }),
  };

  /**
   * Only show the dots on the timeline after the timeline has fully extended
   */
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest >= 0.12) {
      setShowDots(true);
    } else {
      setShowDots(false);
    }
  });

  // The number of dots to show on the timeline
  const numDots = 8;
  const dots = [];

  for (let i = 0; i < numDots; i++) {
    dots.push(
      <div key={i} className='rounded-full bg-zinc-300 shadow-lg flex justify-center items-end h-2 w-2 2xl:h-4 2xl:w-4'>
        {i + 1 === numDots && (
          <div className='relative flex flex-col items-center mb-3 2xl:mb-5'>
            <motion.div className='bg-black z-10 w-[5svw] mb-[2.5svh]' style={{ ...lastItemStyles, aspectRatio: boxAspectRatio }} />
            <motion.div className='absolute bottom-0 bg-white w-px' style={{ height: lineHeight }} />
          </div>
        )}
      </div>,
    );
  }

  /**
   * Update box aspect ratio on window resize
   */
  useEffect(() => {
    window.addEventListener("resize", () => setBoxAspectRatio(window.innerWidth / window.innerHeight));
    return () => {
      window.removeEventListener("resize", () => setBoxAspectRatio(window.innerWidth / window.innerHeight));
    };
  }, []);

  return (
    <motion.div className='h-full w-full flex items-end py-28'>
      <motion.div
        className='absolute mt-auto rounded-full flex items-center justify-end bg-linear-to-r from-zinc-700/60 via-zinc-400/80 to-zinc-700/60 backdrop-blur-sm h-0.5 2xl:h-2'
        style={barStyles}
      >
        <div className='w-2/3 flex justify-around'>{showDots && dots}</div>
      </motion.div>
    </motion.div>
  );
}
